import { useCallback } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { fetchJson } from '@/components/inbox/utils'
import type { ConversationListItem, ConversationDetail, Agent } from '@/components/inbox/types'

interface UseBulkActionsParams {
  conversations: ConversationListItem[]
  setConversations: React.Dispatch<React.SetStateAction<ConversationListItem[]>>
  selectedConversation: ConversationDetail | null
  setSelectedConversation: React.Dispatch<React.SetStateAction<ConversationDetail | null>>
  setStats: React.Dispatch<React.SetStateAction<{ all: number; assigned_to_me: number; unassigned: number; open: number; closed: number; snoozed: number }>>
  agents: Agent[]
  currentAgentId: number
  statusFilter: string
  filteredConversations: ConversationListItem[]
  selectedConversationIds: Set<number>
  setSelectedConversationIds: React.Dispatch<React.SetStateAction<Set<number>>>
}

export function useBulkActions({
  conversations,
  setConversations,
  selectedConversation,
  setSelectedConversation,
  setStats,
  agents,
  currentAgentId,
  statusFilter,
  filteredConversations,
  selectedConversationIds,
  setSelectedConversationIds,
}: UseBulkActionsParams) {

  const toggleConversationSelection = useCallback((id: number) => {
    setSelectedConversationIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const deselectAllConversations = useCallback(() => {
    setSelectedConversationIds(new Set())
  }, [])

  const toggleSelectAll = useCallback(() => {
    setSelectedConversationIds((prev) => {
      if (prev.size === filteredConversations.length) return new Set()
      return new Set(filteredConversations.map((c) => c.id))
    })
  }, [filteredConversations])

  const handleBulkAssign = useCallback(async (agentId: number) => {
    const ids = Array.from(selectedConversationIds)
    const agent = agents.find((a) => a.id === agentId) || null
    const assigningToMe = agentId === currentAgentId
    const affectedConvs = conversations.filter((c) => selectedConversationIds.has(c.id) && c.status === 'open')

    let newlyAssignedToMe = 0, removedFromUnassigned = 0, removedFromAssignedToMe = 0
    for (const c of affectedConvs) {
      if (!c.assigned_agent) removedFromUnassigned++
      if (c.assigned_agent?.id === currentAgentId && !assigningToMe) removedFromAssignedToMe++
      if (assigningToMe && c.assigned_agent?.id !== currentAgentId) newlyAssignedToMe++
    }

    setConversations((prev) => prev.map((c) => (selectedConversationIds.has(c.id) ? { ...c, assigned_agent: agent } : c)))
    if (selectedConversation && selectedConversationIds.has(selectedConversation.id)) {
      setSelectedConversation((prev) => (prev ? { ...prev, assigned_agent: agent } : prev))
    }
    setStats((prev) => ({
      ...prev,
      assigned_to_me: prev.assigned_to_me + newlyAssignedToMe - removedFromAssignedToMe,
      unassigned: prev.unassigned - removedFromUnassigned,
    }))

    const response = await fetchJson('/conversations/bulk_update', {
      method: 'PATCH',
      body: { conversation_ids: ids, conversation: { assigned_agent_id: agentId } },
    })
    if (response.ok) {
      const data = await response.json()
      toast.success(`${data.updated_count} conversa(s) atribuída(s)`)
      setSelectedConversationIds(new Set())
    } else { toast.error('Erro ao atribuir conversas'); router.reload() }
  }, [selectedConversationIds, agents, selectedConversation, conversations, currentAgentId])

  const handleBulkUnassign = useCallback(async () => {
    const ids = Array.from(selectedConversationIds)
    const affectedConvs = conversations.filter((c) => selectedConversationIds.has(c.id) && c.status === 'open')
    let newlyUnassigned = 0, removedFromAssignedToMe = 0
    for (const c of affectedConvs) {
      if (c.assigned_agent) newlyUnassigned++
      if (c.assigned_agent?.id === currentAgentId) removedFromAssignedToMe++
    }

    setConversations((prev) => prev.map((c) => (selectedConversationIds.has(c.id) ? { ...c, assigned_agent: null } : c)))
    if (selectedConversation && selectedConversationIds.has(selectedConversation.id)) {
      setSelectedConversation((prev) => (prev ? { ...prev, assigned_agent: null } : prev))
    }
    setStats((prev) => ({
      ...prev,
      assigned_to_me: prev.assigned_to_me - removedFromAssignedToMe,
      unassigned: prev.unassigned + newlyUnassigned,
    }))

    const response = await fetchJson('/conversations/bulk_update', {
      method: 'PATCH',
      body: { conversation_ids: ids, conversation: { assigned_agent_id: null } },
    })
    if (response.ok) {
      const data = await response.json()
      toast.success(`${data.updated_count} conversa(s) desatribuída(s)`)
      setSelectedConversationIds(new Set())
    } else { toast.error('Erro ao desatribuir conversas'); router.reload() }
  }, [selectedConversationIds, selectedConversation, conversations, currentAgentId])

  const handleBulkStatusChange = useCallback(async (status: 'closed' | 'open') => {
    const ids = Array.from(selectedConversationIds)
    const affectedConvs = conversations.filter((c) => selectedConversationIds.has(c.id))
    const count = affectedConvs.length
    let assignedToMeDelta = 0, unassignedDelta = 0

    for (const c of affectedConvs) {
      if (c.status === 'open' && status === 'closed') {
        if (c.assigned_agent?.id === currentAgentId) assignedToMeDelta--
        if (!c.assigned_agent) unassignedDelta--
      }
      if (c.status === 'closed' && status === 'open') {
        if (c.assigned_agent?.id === currentAgentId) assignedToMeDelta++
        if (!c.assigned_agent) unassignedDelta++
      }
    }

    const shouldRemove =
      (status === 'closed' && statusFilter !== 'closed') ||
      (status === 'open' && statusFilter !== 'open')
    if (shouldRemove) {
      setConversations((prev) => prev.filter((c) => !selectedConversationIds.has(c.id)))
    } else {
      setConversations((prev) => prev.map((c) => (selectedConversationIds.has(c.id) ? { ...c, status } : c)))
    }

    setStats((prev) => {
      const openDelta = status === 'closed' ? -count : count
      return {
        ...prev,
        all: prev.all + openDelta,
        assigned_to_me: prev.assigned_to_me + assignedToMeDelta,
        unassigned: prev.unassigned + unassignedDelta,
        open: prev.open + openDelta,
        closed: prev.closed + (status === 'closed' ? count : -count),
      }
    })

    const response = await fetchJson('/conversations/bulk_update', {
      method: 'PATCH',
      body: { conversation_ids: ids, conversation: { status } },
    })
    if (response.ok) {
      const data = await response.json()
      const action = status === 'closed' ? 'fechada(s)' : 'reaberta(s)'
      toast.success(`${data.updated_count} conversa(s) ${action}`)
      setSelectedConversationIds(new Set())
    } else {
      toast.error(`Erro ao ${status === 'closed' ? 'fechar' : 'reabrir'} conversas`)
      router.reload()
    }
  }, [selectedConversationIds, statusFilter, conversations, currentAgentId])

  return {
    selectedConversationIds,
    setSelectedConversationIds,
    toggleConversationSelection,
    deselectAllConversations,
    toggleSelectAll,
    handleBulkAssign,
    handleBulkUnassign,
    handleBulkStatusChange,
  }
}
